import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setDone(true);
    } catch (ex) {
      toast.error(ex.response?.data?.detail || ex.message || "Request failed");
    } finally { setBusy(false); }
  };
  return (
    <AuthShell testId="forgot-page" kicker="Account recovery" title="Reset your password" subtitle="Enter your email and we'll send a secure reset link." asideKicker="Security" asideTitle="Your login is personal. Reset it any time — links expire in 30 minutes.">
      <form onSubmit={submit} className="w-full space-y-4" autoComplete="off">
        {done ? (
          <div data-testid="fp-done" className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 p-3">
            If <b>{email}</b> is registered with us, we've sent a reset link. Check your inbox — the link expires in 30 minutes.
          </div>
        ) : (
          <>
            <p className="text-sm text-zinc-600">Enter your account email and we'll send you a link to set a new password.</p>
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-600">Email</Label>
              <Input data-testid="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" placeholder="you@example.com" />
            </div>
            <Button data-testid="fp-submit" type="submit" disabled={busy} className="w-full h-11 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-semibold tracking-wide">
              {busy ? "Sending…" : "Send reset link →"}
            </Button>
          </>
        )}
        <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500 text-center pt-1">
          <Link to="/login" className="text-zinc-900 underline">Back to sign in</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/reset-password`, { token, new_password: pw });
      toast.success("Password updated", { description: "Sign in with your new password" });
      setTimeout(() => nav("/login"), 400);
    } catch (ex) {
      toast.error(ex.response?.data?.detail || ex.message || "Reset failed");
    } finally { setBusy(false); }
  };
  return (
    <AuthShell testId="reset-page" kicker="Account recovery" title="Choose a new password" subtitle="Pick a strong password you don't use anywhere else." asideKicker="Security" asideTitle="Almost there — set your new password and sign back in.">
      <form onSubmit={submit} className="w-full space-y-4" autoComplete="off">
        {!token ? (
          <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 p-2">The reset link is missing its token. Request a new one.</div>
        ) : null}
        <div>
          <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-600">New password</Label>
          <Input data-testid="rp-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required autoComplete="new-password" placeholder="Min 6 characters" />
        </div>
        <div>
          <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-600">Confirm password</Label>
          <Input data-testid="rp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" />
        </div>
        <Button data-testid="rp-submit" type="submit" disabled={busy || !token} className="w-full h-11 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-semibold tracking-wide">
          {busy ? "Updating…" : "Update password →"}
        </Button>
        <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500 text-center">
          <Link to="/login" className="text-zinc-900 underline">Back to sign in</Link>
        </div>
      </form>
    </AuthShell>
  );
}
